import { useState } from "react";

import clsx from "clsx";

import {
  DebouncedSearchInput,
  Pill,
  PrimaryIconButton,
  QuickSelectInfoButton,
  TertiaryButton,
} from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics/hooks";
import { prettyLogEvent } from "@internal/analytics";
import { Pages } from "@north-beam/nb-client-side/constants";

import { useSalesV4Context } from "./hooks";
import { MetricFilters } from "./metric-filters";
import { CustomizeTableModal } from "./modals/customize-table-modal";
import { MetricFilterModal } from "./modals/metric-filter-modal";
import { TableDrawer } from "./table-drawer";
import { type TableState } from "./types";

interface Props {
  tableState: TableState;
  onTableStateChange: (tableState: TableState) => void;
  isMaximized?: boolean;
}

export const TableSettings = ({
  tableState,
  onTableStateChange,
  isMaximized,
}: Props) => {
  const [isMetricsModalOpen, setIsMetricsModalOpen] = useState(false);
  const [isCustomizeModalOpen, setIsCustomizeModalOpen] = useState(false);
  const [isMaximizeTableModalOpen, setIsMaximizeTableModalOpen] =
    useState(false);

  const { setShowPercentChangeOnTable } = useSalesV4Context();
  const prettyLogEventPage = usePrettyLogEventPage();

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        {!isMaximized && (
          <PrimaryIconButton
            size="sm"
            onClick={() => {
              setIsMaximizeTableModalOpen(true);
              prettyLogEventPage({
                domain: "Sales Attribution",
                action: "onClick",
                additionalActionInfo: "Maximize Sales Attribution Table",
                data: {
                  pageContext: { ...tableState },
                },
              });
            }}
          >
            <div className="h-[24px] w-[24px] bg-v2-overlay-shadow-200 rounded-[24px] flex items-center justify-center">
              <i className="fa-regular fa-arrow-left-to-line" />
            </div>
          </PrimaryIconButton>
        )}
        <DebouncedSearchInput
          wrapperClassName="w-[240px]"
          value={tableState.searchText}
          onChange={(value) =>
            onTableStateChange({
              ...tableState,
              searchText: value,
              pageIndex: 0,
            })
          }
        />
        <div className="flex flex-row gap-3">
          {tableState.breakdownSelection.length > 0 && (
            <Pill
              variant="notice"
              number={tableState.breakdownSelection.length}
              onClear={() => {
                onTableStateChange({ ...tableState, breakdownSelection: [] });
              }}
            >
              Breakdowns
            </Pill>
          )}
          {tableState.campaignSelection.length > 0 && (
            <Pill
              variant="notice"
              number={tableState.campaignSelection.length}
              onClear={() => {
                onTableStateChange({ ...tableState, campaignSelection: [] });
              }}
            >
              Campaigns
            </Pill>
          )}
          {tableState.adsetSelection.length > 0 && (
            <Pill
              variant="notice"
              number={tableState.adsetSelection.length}
              onClear={() => {
                onTableStateChange({ ...tableState, adsetSelection: [] });
              }}
            >
              Adsets
            </Pill>
          )}
          {tableState.adSelection.length > 0 && (
            <Pill
              variant="notice"
              number={tableState.adSelection.length}
              onClear={() => {
                onTableStateChange({ ...tableState, adSelection: [] });
              }}
            >
              Ads
            </Pill>
          )}
        </div>
      </div>
      <div className="flex gap-0.5">
        <MetricFilters
          metricFilters={tableState.metricFilters}
          onClear={() => {
            onTableStateChange({
              ...tableState,
              metricFilters: [],
              pageIndex: 0,
            });
          }}
          onModalOpen={() => setIsMetricsModalOpen(true)}
        />
        <TertiaryButton
          className="uppercase"
          size="sm"
          onClick={() => setIsCustomizeModalOpen(true)}
        >
          Customize
          <i className="fa-regular fa-gear"></i>
        </TertiaryButton>
        <QuickSelectInfoButton
          selected={tableState.showPercentChange}
          className={clsx(
            "w-8 h-8 px-0 text-xs text-v2-action hover:text-v2-action-hover",
            !tableState.showPercentChange && "border-0",
          )}
          onClick={() => {
            setShowPercentChangeOnTable(!tableState.showPercentChange);
          }}
        >
          <i className="fa-solid fa-percent"></i>
        </QuickSelectInfoButton>
      </div>
      <MetricFilterModal
        isOpen={isMetricsModalOpen}
        metricFilters={tableState.metricFilters}
        tableState={tableState}
        onChange={(metricFilters) => {
          onTableStateChange({ ...tableState, metricFilters, pageIndex: 0 });
        }}
        onClose={() => setIsMetricsModalOpen(false)}
      />
      <CustomizeTableModal
        isOpen={isCustomizeModalOpen}
        selectedMetricIds={tableState.selectedMetricIds}
        onChange={(selectedMetricIds) =>
          onTableStateChange({ ...tableState, selectedMetricIds })
        }
        onClose={() => setIsCustomizeModalOpen(false)}
      />
      <TableDrawer
        tableState={isMaximizeTableModalOpen ? tableState : undefined}
        index={0}
        onClose={() => setIsMaximizeTableModalOpen(false)}
        onTableStateChange={onTableStateChange}
      />
    </div>
  );
};
